import React from 'react';

/**
 * The moving half of the app's ground: six slow aurora fields, two blurred
 * ribbon layers crossing each other, and eight motes, two of which travel.
 *
 * Mounted once at the root and never re-rendered — it sits outside the page
 * container so navigation cannot touch it. The static half of the same light
 * is painted on <html> by variant-c/canvas.css, which is what stops the first
 * frame from flashing dark before this arrives.
 *
 * Memoised with no props: React then has nothing to diff, so eight compositor
 * layers are rasterised once for the life of the session. Everything
 * that moves animates transform only; the stylesheet turns all of it off under
 * prefers-reduced-motion and below 900px.
 *
 * The ribbon stops are literal hexes rather than `var(--teal)` and friends, and
 * that is deliberate rather than an oversight — the one place in the migration
 * where a colour is restated instead of read from a token. This is a decorative
 * light field, not a surface anything is read against, and the demo tunes it for
 * the light theme by *dimming the layer* rather than by swapping the palette:
 * `:root[data-theme="light"] .bg-ribbons { opacity: .3 }` and `.two { .22 }`,
 * and again below 900px. Those hues at those opacities are what was tuned by
 * eye. Point the stops at tokens and the light theme gets the light palette at
 * the dark palette's opacities, which is a different picture from the one that
 * was approved. If this layer is ever re-tuned, re-tune it in the demo — the
 * stops here and in `design-demos/variant-c/app.js` are the same three
 * gradients.
 */
const AppBackground = React.memo(function AppBackground() {
    return (
        <div className="bg" aria-hidden="true">
            <span className="aurora a1" />
            <span className="aurora a2" />
            <span className="aurora a3" />
            <span className="aurora a4" />
            <span className="aurora a5" />
            <span className="aurora a6" />

            <svg className="bg-ribbons" viewBox="0 0 1200 800" preserveAspectRatio="none">
                <defs>
                    <linearGradient id="vcRibbonA" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#00d4aa" stopOpacity="0" />
                        <stop offset="45%" stopColor="#00d4aa" stopOpacity=".55" />
                        <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient id="vcRibbonB" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#ec4899" stopOpacity="0" />
                        <stop offset="55%" stopColor="#8b5cf6" stopOpacity=".45" />
                        <stop offset="100%" stopColor="#38bdf8" stopOpacity="0" />
                    </linearGradient>
                    {/* blurred inside the SVG so the blur is painted into the layer once */}
                    <filter id="vcRibbonBlurA" filterUnits="userSpaceOnUse" x="-200" y="-200" width="1600" height="1200">
                        <feGaussianBlur stdDeviation="9" />
                    </filter>
                </defs>
                <g filter="url(#vcRibbonBlurA)">
                    <path className="rb1" stroke="url(#vcRibbonA)" d="M-100,520 C220,470 340,250 640,240 C900,232 1000,420 1320,330" />
                    <path className="rb2" stroke="url(#vcRibbonB)" d="M-100,690 C260,660 420,470 760,500 C1020,522 1120,650 1320,600" />
                </g>
            </svg>

            {/* a second layer, slower and blurrier, so the arcs read as depth */}
            <svg className="bg-ribbons two" viewBox="0 0 1200 800" preserveAspectRatio="none">
                <defs>
                    <linearGradient id="vcRibbonC" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#38bdf8" stopOpacity="0" />
                        <stop offset="50%" stopColor="#38bdf8" stopOpacity=".5" />
                        <stop offset="100%" stopColor="#ec4899" stopOpacity="0" />
                    </linearGradient>
                    <filter id="vcRibbonBlurC" filterUnits="userSpaceOnUse" x="-200" y="-200" width="1600" height="1200">
                        <feGaussianBlur stdDeviation="14" />
                    </filter>
                </defs>
                <path className="rb3" filter="url(#vcRibbonBlurC)" stroke="url(#vcRibbonC)" d="M-100,180 C280,120 460,330 800,300 C1040,278 1160,150 1320,120" />
            </svg>

            <span className="mote m1" />
            <span className="mote m2" />
            <span className="mote m3" />
            <span className="mote m4" />
            <span className="mote m5" />
            <span className="mote m6" />
            <span className="mote drifter d1" />
            <span className="mote drifter d2" />
        </div>
    );
});

export default AppBackground;
